import { useState, type ReactNode } from 'react';
import { line, Size, t, trackingUrlProblem, type ShipFormAnswer, type ShipPackagesAnswer, type ShipPackagesRequest, type SuggestionRow } from '@bms2/shared';
import { Checkbox, DatePicker, Dropdown, NumericInput, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { useDocumentToday } from '../../shell/ViewerZone';
import { RefusalText } from './RefusalText';
import { PACKAGE_SHIPMENTS_PATH, PACKAGE_SHIP_FORM_PATH, post, useApi } from './api';
import styles from './PackageWindows.module.css';

/**
 * 🔒 The carrier picker (decision 1731): the carriers the shipments already
 * name, filtered by what is typed, and the typed name itself offered first
 * where it is new, so a carrier nobody has used is taken as it is written.
 */
export function carrierSuggestions(carriers: readonly string[], query: string): SuggestionRow[] {
  const typed = query.trim();
  const matching = carriers.filter((name) => name.toLowerCase().includes(typed.toLowerCase()));
  const known = carriers.some((name) => name.toLowerCase() === typed.toLowerCase());
  const rows = matching.map((name) => ({ id: name, line1: name }));
  return typed && !known ? [{ id: typed, line1: typed, line2: t('shipment.window.newCarrier') }, ...rows] : rows;
}

/** 🔒 The line a saved shipment notices: its number, and a failed customer notice named beside it (T1141), since the window has closed by then. */
export function shippedNotice(answer: ShipPackagesAnswer): string {
  return line(t('shipment.window.shipped'), answer.shipmentNumber, answer.notified === 'Failed' ? `· ${t('shipment.window.noticeFailed')}` : '');
}

/**
 * 🔒 The shipment window (decision 1731), the New Shipment form of the owner's
 * Zoho screen: the order's Not Shipped packages, the ship date, the carrier
 * picked or typed, the tracking number and its address, the shipping charge,
 * the site the stock leaves, and the delivered and notify ticks. Saving writes
 * and confirms the shipment in one press.
 *
 * 🔒 An order with no Not Shipped package opens the refusal instead, offering
 * New package (decision 1730).
 */
export function ShipPackagesModal({ salesOrderId, preselected = [], apiBaseUrl = '', onCancel, onShipped, onNewPackage }: {
  salesOrderId: string;
  /** The packages ticked when the window opens: the one a package's own Ship opened it from. */
  preselected?: readonly string[];
  apiBaseUrl?: string;
  onCancel: () => void;
  onShipped: (answer: ShipPackagesAnswer) => void;
  /** Where the refusal's New package goes. */
  onNewPackage: () => void;
}) {
  const form = useApi<ShipFormAnswer>(`${PACKAGE_SHIP_FORM_PATH}?salesOrderId=${encodeURIComponent(salesOrderId)}`, apiBaseUrl);
  if (form.state.kind === 'ready' && form.state.data.packages.length === 0) {
    return (
      <Modal
        open
        title={t('shipment.needsPackage.title')}
        onCancel={onCancel}
        dataModal="shipment-needs-package"
        actions={[
          { kind: 'primary', label: t('shipment.needsPackage.newPackage'), onAct: onNewPackage },
          { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
        ]}
      >
        <p className={modalStyles.fieldLabel} data-shipment-needs-package="true">{t('shipment.needsPackage.message')}</p>
      </Modal>
    );
  }
  if (form.state.kind === 'ready') return <ShipWindow answer={form.state.data} preselected={preselected} apiBaseUrl={apiBaseUrl} onCancel={onCancel} onShipped={onShipped} />;
  return (
    <Modal open title={t('shipment.window.title')} onCancel={onCancel} dataModal="ship-packages" actions={[
      // 🔒 One Primary always (piece 5): Try again once the read failed, else greyed with the reason while it is in flight (decision 0559).
      form.state.kind === 'failed'
        ? { kind: 'primary', label: t('package.window.retry'), onAct: form.reload }
        : { kind: 'primary', label: t('shipment.window.save'), onAct: form.reload, disabled: true, disabledReason: t('package.window.loading') },
      { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
    ]}>
      {form.state.kind === 'failed' ? <AlertBanner tone="error" message={form.state.error} /> : <p className={modalStyles.fieldLabel}>{t('package.window.loading')}</p>}
    </Modal>
  );
}

function ShipWindow({ answer, preselected, apiBaseUrl, onCancel, onShipped }: {
  answer: ShipFormAnswer;
  preselected: readonly string[];
  apiBaseUrl: string;
  onCancel: () => void;
  onShipped: (answer: ShipPackagesAnswer) => void;
}) {
  const today = useDocumentToday();
  const opening = preselected.filter((id) => answer.packages.some((p) => p.id === id));
  // 🔒 One package on the order is the one the shipment takes; with several, the one Ship was pressed on, or none ticked yet.
  const [chosen, setChosen] = useState<readonly string[]>(opening.length > 0 ? opening : answer.packages.length === 1 ? [answer.packages[0]!.id] : []);
  const [shippedOn, setShippedOn] = useState(today);
  const [carrier, setCarrier] = useState<string | null>(null);
  const [tracking, setTracking] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [charges, setCharges] = useState<number | null>(null);
  const [origin, setOrigin] = useState(answer.defaultOriginSiteId ?? '');
  const [delivered, setDelivered] = useState(false);
  const [notify, setNotify] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [error, setError] = useState<ReactNode>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id: string, on: boolean) => setChosen((was) => (on ? [...was, id] : was.filter((x) => x !== id)));
  const save = async () => {
    if (chosen.length === 0) return setError(t('shipment.window.needsPackage'));
    if (!shippedOn) return setError(t('ship.confirm.needsDate'));
    if (!tracking.trim()) return setError(t('ship.confirm.needsTracking'));
    if (answer.originSites.length > 0 && !origin) return setError(t('ship.confirm.needsOrigin'));
    const urlProblem = trackingUrlProblem(trackingUrl);
    if (urlProblem) return setError(t('shipment.window.badTrackingUrl'));
    if (notify && !notifyEmail.trim()) return setError(t('shipment.window.needsEmail'));
    const body: ShipPackagesRequest = {
      salesOrderId: answer.salesOrderId, packageIds: [...chosen], shippedOn, carrier: carrier?.trim() || null, trackingNumber: tracking.trim(), trackingUrl: trackingUrl.trim() || null,
      shippingChargesCents: charges, originSiteId: origin || null, delivered, notifyEmail: notify ? notifyEmail.trim() : null,
    };
    setBusy(true);
    const shipped = await post<ShipPackagesAnswer>(PACKAGE_SHIPMENTS_PATH, body, apiBaseUrl);
    setBusy(false);
    if (!shipped.ok) return setError(<RefusalText message={shipped.message} body={shipped.body} />);
    onShipped(shipped.data);
  };

  return (
    <Modal
      open
      title={t('shipment.window.title')}
      subtitle={line(t('package.window.forOrder'), answer.salesOrderNumber)}
      onCancel={onCancel}
      dataModal="ship-packages"
      width="wide"
      actions={[
        { kind: 'primary', label: t('shipment.window.save'), onAct: () => void save(), disabled: busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={modalStyles.fieldLabel}>{t('shipment.window.packages')}</p>
      <div className={styles.choices} data-ship-packages={answer.packages.length}>
        {answer.packages.map((p) => (
          <span key={p.id} data-ship-package={p.id}>
            <Checkbox label={line(p.packageNumber, '·', String(p.totalQuantity), t('shipment.window.units'))} checked={chosen.includes(p.id)} onChange={(on) => toggle(p.id, on)} name={`ship-${p.id}`} />
          </span>
        ))}
      </div>
      <div className={modalStyles.pair}>
        <DatePicker size={Size.Small} label={t('ship.confirm.shippedOn')} value={shippedOn} onChange={setShippedOn} mode="date" name="shippedOn" required />
        <Dropdown
          size={Size.Medium}
          label={t('ship.confirm.carrier')}
          entityLabel={t('shipment.window.carrierEntity')}
          search={async (query) => carrierSuggestions(answer.carriers, query)}
          value={carrier}
          onSelect={(row) => setCarrier(row.id)}
          onAddNew={null}
        />
      </div>
      <div className={modalStyles.pair}>
        <TextInput size={Size.Medium} label={t('ship.confirm.tracking')} value={tracking} onChange={setTracking} name="trackingNumber" required />
        <TextInput size={Size.Medium} label={t('shipment.window.trackingUrl')} value={trackingUrl} onChange={setTrackingUrl} name="trackingUrl" />
      </div>
      <div className={modalStyles.pair}>
        <NumericInput size={Size.Small} label={t('shipment.window.charges')} kind="currency" value={charges} onChange={setCharges} name="shippingCharges" />
        {answer.originSites.length > 0 ? (
          <Select size={Size.Medium} label={t('ship.confirm.origin')} required value={origin} onChange={setOrigin} placeholder={t('customFieldsModal.none')} options={answer.originSites.map((site) => ({ value: site.id, label: site.label }))} dataAttribute={{ 'data-ship-origin': 'true' }} />
        ) : null}
      </div>
      <Checkbox label={t('shipment.window.delivered')} checked={delivered} onChange={setDelivered} name="delivered" />
      <Checkbox label={t('shipment.window.notify')} checked={notify} onChange={setNotify} name="notify" />
      {notify ? <TextInput size={Size.Medium} label={t('shipment.window.notifyEmail')} value={notifyEmail} onChange={setNotifyEmail} name="notifyEmail" required /> : null}
    </Modal>
  );
}
